"use client";

import React, { useState, useMemo, useEffect } from "react";
import {
  Users,
  Briefcase,
  Sofa,
  CalendarDays,
  Circle,
  MapPin,
  Building2,
  Calendar,
  Star,
  ChevronLeft,
  ChevronRight,
  Locate,
  User,
} from "lucide-react";

import { useHistory } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../shadcn/ui/tooltip";

// Component for space image with fallback icon
const SpaceImageWithFallback = ({ space, size = "sm", className = "" }) => {
  const [hasError, setHasError] = useState(false);

  // Size variants for different use cases
  const sizeClasses = {
    xs: "w-6 h-6", // 24px
    sm: "w-8 h-8 sm:w-10 sm:h-10", // 32px/40px
    md: "w-12 h-12 sm:w-16 sm:h-16", // 48px/64px
    lg: "w-20 h-20 sm:w-24 sm:h-24", // 80px/96px
    xl: "w-32 h-32", // 128px
  };

  const iconSizes = {
    xs: "w-3 h-3",
    sm: "w-4 h-4 sm:w-5 sm:h-5",
    md: "w-6 h-6 sm:w-8 sm:h-8",
    lg: "w-10 h-10 sm:w-12 sm:h-12",
    xl: "w-16 h-16",
  };

  if (!space.image || hasError) {
    return (
      <div
        className={`flex flex-shrink-0 justify-center items-center bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg ${sizeClasses[size]} ${className}`}
      >
        <Building2 className={`text-blue-400 ${iconSizes[size]}`} />
      </div>
    );
  }

  return (
    <img
      src={space.image}
      alt={space.name}
      className={`object-cover flex-shrink-0 rounded-lg ${sizeClasses[size]} ${className}`}
      onError={() => setHasError(true)}
    />
  );
};

// Helper to extract image URLs from space object
const extractImageUrls = space => {
  if (space?.media?.images && Array.isArray(space.media.images) && space.media.images.length > 0) {
    return space.media.images.map(img => (typeof img === "string" ? img : img.url)).filter(Boolean);
  }
  if (space?.images && Array.isArray(space.images) && space.images.length > 0) {
    return space.images.map(img => (typeof img === "string" ? img : img.url || img)).filter(Boolean);
  }
  if (space?.image) {
    return [space.image];
  }
  return ["/img/placeholder-space.jpg"];
};

// Airbnb-style image carousel component
const SpaceImageCarousel = React.memo(
  ({ space, className = "", autoCycle = true, isHovered = false }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [imageErrors, setImageErrors] = useState(new Set());

    // Create a stable string key from image URLs to prevent unnecessary recalculations
    const imageUrlsKey = extractImageUrls(space).join("|");

    // Get images array - memoized based on the actual URL content, not object references
    const images = useMemo(() => {
      return imageUrlsKey.split("|");
    }, [imageUrlsKey]);

    // Auto-cycle through images on hover — 1.5s snappy feel
    useEffect(() => {
      if (!autoCycle || !isHovered || images.length <= 1) return;
      const interval = setInterval(() => {
        setCurrentImageIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
      }, 1500);
      return () => clearInterval(interval);
    }, [images.length, autoCycle, isHovered]);

    const handleImageError = index => {
      setImageErrors(prev => new Set([...prev, index]));
    };

    const handlePrevImage = e => {
      e.stopPropagation();
      setCurrentImageIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleNextImage = e => {
      e.stopPropagation();
      setCurrentImageIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
    };

    const goToImage = (index, e) => {
      e.stopPropagation();
      setCurrentImageIndex(index);
    };

    // Don't show carousel if only one image
    if (images.length <= 1) {
      const imgSrc = images[0];
      const isPlaceholder = !imgSrc || imgSrc === "/img/placeholder-space.jpg";
      const hasError = imageErrors.has(0);

      if (isPlaceholder || hasError) {
        return (
          <div className={`flex w-full h-full items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg ${className}`}>
            <Building2 className="w-12 h-12 text-blue-400" />
          </div>
        );
      }

      return (
        <img
          src={imgSrc}
          alt={space.name}
          className={`w-full h-full object-cover rounded-lg ${className}`}
          onError={() => handleImageError(0)}
        />
      );
    }

    const currentImage = images[currentImageIndex];
    const hasError = imageErrors.has(currentImageIndex);

    if (hasError || !currentImage || currentImage === "/img/placeholder-space.jpg") {
      return (
        <div className={`flex w-full h-full items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg ${className}`}>
          <Building2 className="w-12 h-12 text-blue-400" />
        </div>
      );
    }

    return (
      <div className={`relative w-full h-full overflow-hidden rounded-lg ${className}`}>
        {/* Main Image — crossfade by keying on index */}
        <img
          key={currentImageIndex}
          src={currentImage}
          alt={`${space.name} - Image ${currentImageIndex + 1}`}
          className="w-full h-full object-cover animate-[fadeIn_.3s_ease]"
          onError={() => handleImageError(currentImageIndex)}
        />

        {/* Navigation Arrows - Show on hover */}
        {images.length > 1 && (
          <>
            <button
              onClick={e => { e.stopPropagation(); handlePrevImage(e); }}
              className={`absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg transition-all duration-200 ${
                isHovered ? "opacity-100" : "opacity-0"
              }`}
              aria-label="Previous image"
            >
              <ChevronLeft className="w-4 h-4 text-gray-700" />
            </button>
            <button
              onClick={e => { e.stopPropagation(); handleNextImage(e); }}
              className={`absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg transition-all duration-200 ${
                isHovered ? "opacity-100" : "opacity-0"
              }`}
              aria-label="Next image"
            >
              <ChevronRight className="w-4 h-4 text-gray-700" />
            </button>
          </>
        )}

        {/* Dot indicators */}
        {images.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={e => { e.stopPropagation(); goToImage(index, e); }}
                className={`transition-all duration-200 rounded-full ${
                  index === currentImageIndex ? "w-4 h-1.5 bg-white shadow" : "w-1.5 h-1.5 bg-white/55 hover:bg-white/80"
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison based on actual image content, not object reference
    const prevUrls = extractImageUrls(prevProps.space).join("|");
    const nextUrls = extractImageUrls(nextProps.space).join("|");
    return (
      prevUrls === nextUrls &&
      prevProps.className === nextProps.className &&
      prevProps.autoCycle === nextProps.autoCycle &&
      prevProps.isHovered === nextProps.isHovered
    );
  }
);

// Helper component for status/type tags
const InfoTag = ({ text, icon, color, type = "status" }) => {
  let bgColor, textColor, dotColor;

  if (type === "status") {
    switch (color) {
      case "green":
        bgColor = "bg-green-100";
        textColor = "text-green-700";
        dotColor = "text-green-500";
        break;
      case "red":
        bgColor = "bg-red-100";
        textColor = "text-red-700";
        dotColor = "text-red-500";
        break;
      case "orange":
        bgColor = "bg-orange-100";
        textColor = "text-orange-700";
        dotColor = "text-orange-500";
        break;
      case "blue":
        bgColor = "bg-purple-100";
        textColor = "text-purple-700";
        dotColor = "text-purple-500";
        break;
      default:
        bgColor = "bg-gray-100";
        textColor = "text-gray-700";
        dotColor = "text-gray-500";
    }
  } else {
    // type === 'type'
    bgColor = "bg-gray-100";
    textColor = "text-gray-700";
  }

  return (
    <div className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${bgColor} ${textColor}`}>
      {type === "status" && <Circle className={`w-2 h-2 mr-1.5 ${dotColor}`} fill={dotColor} />}
      {icon}
      {text}
    </div>
  );
};

const SpaceCard = React.memo(
  ({
    space,
    onClick,
    onBookSpace,
    featuredToggle,
    onEdit,
    onDelete,
    onTogglePublish,
    onToggleFeatured,
    onZoomTo,
    onHostClick,
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isPressed, setIsPressed] = useState(false);
    // Debug logging removed to reduce re-renders

    const history = useHistory();
    // Host organization id used to navigate to the public host profile page (/hosts/:hostId)
    const hostOrgId = space?.organization?.id || space?.hostOrganizationId || space?.organizationId;
    const canOpenHostProfile = Boolean(hostOrgId);
    const hostName = space?.organization?.name || space?.hostDisplayName || space?.organizationName;
    const hostLogo = space?.organization?.logoUrl || space?.hostDisplayImage || space?.organizationLogoUrl;

    const handleHostNavigate = (e) => {
      e.stopPropagation();
      if (onHostClick) {
        onHostClick(space);
      } else if (hostOrgId) {
        history.push(`/hosts/${hostOrgId}`);
      }
    };

    const getTypeIcon = type => {
      switch (type) {
        case "Meeting Room":
        case "Conference Room":
          return <Users className="w-3 h-3 mr-1.5" />;
        case "Desk":
        case "Private Office":
          return <Briefcase className="w-3 h-3 mr-1.5" />;
        case "Lounge":
          return <Sofa className="w-3 h-3 mr-1.5" />;
        default:
          return <CalendarDays className="w-3 h-3 mr-1.5" />;
      }
    };

    const getStatusColor = status => {
      switch (status) {
        case "PUBLISHED":
          return "bg-green-500 text-white";
        case "UNPUBLISHED":
          return "bg-orange-500 text-white";
        case "DRAFT":
          return "bg-gray-500 text-white"; // legacy value; backend not using DRAFT
        default:
          return "bg-gray-500 text-white";
      }
    };

    const getStatusDisplay = status => {
      switch (status) {
        case "PUBLISHED":
          return "Live";
        case "UNPUBLISHED":
          return "Draft";
        case "DRAFT":
          return "Draft"; // normalize legacy value to Draft
        default:
          return "Draft";
      }
    };

    // Map from our dummy data to the structure needed for the card
    const spaceType = space.type || "Meeting Room";
    const statusColor = getStatusColor(space.status || "UNPUBLISHED");

    return (
      <div>
        <div
          className={`group overflow-hidden bg-white rounded-2xl border border-gray-100 cursor-pointer transition-all duration-200 ease-out shadow-sm ${
            isPressed ? "scale-[0.98] shadow-inner" : "hover:shadow-xl hover:shadow-gray-200/60 hover:-translate-y-0.5 hover:border-gray-200"
          }`}
          onClick={() => onClick && onClick(space)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => { setIsHovered(false); setIsPressed(false); }}
          onMouseDown={() => setIsPressed(true)}
          onMouseUp={() => setIsPressed(false)}
          onTouchStart={() => setIsPressed(true)}
          onTouchEnd={() => setIsPressed(false)}
        >
          {/* ── Image area ── */}
          <div className="relative h-52 sm:h-56 overflow-hidden">
            <SpaceImageCarousel space={space} className="w-full h-full" isHovered={isHovered} />

            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/5 to-transparent pointer-events-none" />

            {/* Admin-only: status toggle badge (top-right) */}
            {onTogglePublish && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={e => { e.stopPropagation(); onTogglePublish(space); }}
                      className={`absolute top-3 right-3 px-2.5 py-1 text-[11px] font-bold rounded-full backdrop-blur-sm transition-colors ${
                        space.status === "PUBLISHED"
                          ? "bg-emerald-500/90 text-white hover:bg-emerald-600"
                          : "bg-amber-500/90 text-white hover:bg-amber-600"
                      }`}
                    >
                      {space.status === "PUBLISHED" ? "Live" : "Draft"}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent><p>{space.status === "PUBLISHED" ? "Mark as draft" : "Publish"}</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Admin-only: featured star (top-left) */}
            {onToggleFeatured && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={e => { e.stopPropagation(); onToggleFeatured(space); }}
                      className="absolute top-3 left-3 p-1.5 rounded-full bg-black/30 backdrop-blur-sm hover:bg-black/50 transition-colors"
                    >
                      <Star className={`w-3.5 h-3.5 ${space.featured ? "text-yellow-400 fill-yellow-400" : "text-white"}`} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent><p>{space.featured ? "Remove featured" : "Mark as featured"}</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Bottom overlay: category chip left, units right */}
            <div className="absolute bottom-0 inset-x-0 flex items-end justify-between px-3 pb-3 pointer-events-none">
              {(space.category?.name || space.subCategory?.name) && (
                <span className="px-2.5 py-1 text-[11px] font-semibold text-white bg-black/45 backdrop-blur-sm rounded-full">
                  {space.subCategory?.name || space.category?.name}
                </span>
              )}
              {(space.instanceCount || space.instances?.length) > 0 && (
                <span className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-white bg-black/45 backdrop-blur-sm rounded-full">
                  <Building2 className="w-3 h-3" />
                  {space.instanceCount || space.instances?.length}
                </span>
              )}
            </div>
          </div>

          {/* ── Body ── */}
          <div className="p-4">
            {/* Space name */}
            <h3 className="text-[15px] font-bold text-gray-900 leading-snug mb-1 line-clamp-2">
              {space.name}
            </h3>

            {/* Location */}
            <div className="flex items-center gap-1 text-gray-400 mb-3">
              <MapPin className="shrink-0 w-3.5 h-3.5" />
              <span className="text-xs truncate">
                {typeof space.location === "object"
                  ? space.location.address || "Location not specified"
                  : space.location || "Location not specified"}
              </span>
              {onZoomTo && space.coordinates?.latitude && space.coordinates?.longitude && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={e => { e.stopPropagation(); onZoomTo(space); }}
                        className="shrink-0 ml-1 p-1 bg-blue-50 hover:bg-blue-100 text-blue-500 rounded transition-colors pointer-events-auto"
                      >
                        <Locate className="w-3 h-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent><p>Zoom to map</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>

            {/* Chips: capacity */}
            {space.capacity > 1 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium text-gray-600 bg-gray-100 rounded-full">
                  <Users className="w-3 h-3" />
                  {space.capacity} people
                </span>
              </div>
            )}

            {/* ── Footer: pricing + book ── */}
            <div className="border-t border-gray-100 pt-3">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium mb-0.5">Price</p>
                  <p className="text-base font-extrabold text-gray-900 leading-none">{space.pricing || "—"}</p>
                </div>
                {onBookSpace && (
                  <button
                    onClick={e => { e.stopPropagation(); onBookSpace(space); }}
                    className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-150 ${
                      space.status === "PUBLISHED"
                        ? "bg-blue-600 text-white hover:bg-blue-700 shadow-sm shadow-blue-200"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                    disabled={space.status !== "PUBLISHED"}
                  >
                    <Calendar className="w-3.5 h-3.5" />
                    {space.status === "PUBLISHED" ? "Book" : "Unavailable"}
                  </button>
                )}
              </div>

              {/* Host row */}
              {canOpenHostProfile && hostName ? (
                <button
                  onClick={handleHostNavigate}
                  className="flex items-center gap-2.5 mt-2 group/host text-left w-full pointer-events-auto"
                >
                  {hostLogo ? (
                    <img
                      src={hostLogo}
                      alt={hostName}
                      className="w-8 h-8 rounded-full object-cover shrink-0 ring-1 ring-gray-100 shadow-sm"
                      onError={e => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 shrink-0">
                      <User className="w-4 h-4 text-blue-500" />
                    </span>
                  )}
                  <div className="min-w-0">
                    <span className="text-[11px] text-gray-400 font-medium">Hosted by </span>
                    <span className="text-xs font-semibold text-gray-600 group-hover/host:text-blue-600 transition-colors">
                      {hostName}
                    </span>
                  </div>
                </button>
              ) : (
                !canOpenHostProfile && hostName && (
                  <div className="flex items-center gap-2.5 mt-2">
                    {hostLogo && (
                      <img
                        src={hostLogo}
                        alt={hostName}
                        className="w-8 h-8 rounded-full object-cover shrink-0 ring-1 ring-gray-100 shadow-sm"
                        onError={e => { e.target.style.display = "none"; }}
                      />
                    )}
                    <span className="text-xs text-gray-400 truncate">Hosted by {hostName}</span>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    );

  },
  (prevProps, nextProps) => {
    // Custom comparison: only re-render if space identity or relevant data changes
    return (
      prevProps.space?.id === nextProps.space?.id &&
      prevProps.space?.image === nextProps.space?.image &&
      prevProps.space?.status === nextProps.space?.status &&
      prevProps.space?.featured === nextProps.space?.featured
    );
  }
);

export { SpaceImageCarousel };
export default SpaceCard;
